import React, { useState, useEffect } from 'react';
import { personAPI } from '../services/api';
import PersonalInfoTab from './tabs/PersonalInfoTab';
import SocialMediaTab from './tabs/SocialMediaTab';
import KnownPeopleTab from './tabs/KnownPeopleTab';
import ImportantInfoTab from './tabs/ImportantInfoTab';
import FilesTab from './tabs/FilesTab';
import GalleryTab from './tabs/GalleryTab';
import TikTokTab from './tabs/TikTokTab';
import InstagramTab from './tabs/InstagramTab';
import OSINTTab from './tabs/OSINTTab';
import { useNotification } from '../context/NotificationContext';

const PersonDetails = ({ person, onUpdate, onDelete }) => {
  const [activeTab, setActiveTab] = useState('personal');
  const [editedPerson, setEditedPerson] = useState(person);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ actions: true });
  const { addNotification } = useNotification();

  // Reset local state when switching between persons
  useEffect(() => {
    setEditedPerson(person);
    setActiveTab('personal');
  }, [person]);

  // When opening TikTok or Instagram tab, refresh the person from API to ensure latest media
  useEffect(() => {
    const refreshIfMedia = async () => {
      if (activeTab !== 'tiktok' && activeTab !== 'instagram') return;
      try {
        const response = await personAPI.getById(person.id);
        setEditedPerson(response.data);
      } catch (e) {
        // ignore fetch errors to avoid blocking UI
      }
    };
    refreshIfMedia();
  }, [activeTab, person.id]);

  const tabs = [
    { id: 'personal', name: 'PERSONAL', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
    { id: 'social', name: 'SOCIAL', icon: 'M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84' },
    { id: 'known', name: 'KNOWN', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
    { id: 'important', name: 'IMPORTANT', icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { id: 'osint', name: 'OSINT', icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' },
    { id: 'files', name: 'FILES', icon: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
    { id: 'gallery', name: 'GALLERY', icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { id: 'tiktok', name: 'TIKTOK', icon: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' },
    { id: 'instagram', name: 'INSTAGRAM', icon: 'M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z M15 13a3 3 0 11-6 0 3 3 0 016 0z' },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await personAPI.update(person.id, editedPerson);
      onUpdate(response.data);
    } catch (error) {
      console.error('Error saving person:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await personAPI.delete(person.id);
      onDelete(person.id);
    } catch (error) {
      console.error('Error deleting person:', error);
    }
  };

  const updateField = (field, value) => {
    setEditedPerson({ ...editedPerson, [field]: value });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className="h-full flex flex-col">
      {/* Top Row: Compact Tabs */}
      <div className="bg-tech-surface border-b border-tech-border px-4">
        <div className="flex items-center justify-between">
          {/* Tabs - horizontal scrollable */}
          <div className="flex space-x-1 overflow-x-auto py-2 scrollbar-thin">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-xs font-mono font-medium whitespace-nowrap rounded transition flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'bg-tech-accent/10 text-tech-accent border border-tech-accent/30'
                    : 'text-tech-textMuted hover:text-tech-text hover:bg-tech-surface'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                </svg>
                <span>{tab.name}</span>
              </button>
            ))}
          </div>

          {/* Actions - collapsible */}
          <div className="shrink-0 ml-4">
            <button
              onClick={() => toggleSection('actions')}
              className="p-2 text-tech-textMuted hover:text-tech-text rounded transition"
              title={expandedSections.actions ? 'Hide actions' : 'Show actions'}
            >
              <svg className={`w-4 h-4 transition-transform ${expandedSections.actions ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Collapsible Actions */}
        {expandedSections.actions && (
          <div className="flex items-center justify-end gap-3 pb-3 animate-fade-in">
            <button
              onClick={() => setDeleteConfirm(true)}
              className="tech-btn-danger py-2 text-xs"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>DELETE</span>
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="tech-btn-primary py-2 text-xs disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>{saving ? 'SAVING...' : 'SAVE'}</span>
            </button>
          </div>
        )}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto min-h-0">
        {activeTab === 'personal' && (
          <PersonalInfoTab person={editedPerson} updateField={updateField} />
        )}
        {activeTab === 'social' && (
          <SocialMediaTab person={editedPerson} updateField={updateField} />
        )}
        {activeTab === 'known' && (
          <KnownPeopleTab person={editedPerson} updateField={updateField} />
        )}
        {activeTab === 'important' && (
          <ImportantInfoTab person={editedPerson} updateField={updateField} />
        )}
        {activeTab === 'files' && (
          <FilesTab person={editedPerson} updateField={updateField} />
        )}
        {activeTab === 'gallery' && (
          <GalleryTab person={editedPerson} updateField={updateField} />
        )}
        {activeTab === 'tiktok' && (
          <TikTokTab person={editedPerson} updateField={updateField} />
        )}
        {activeTab === 'instagram' && (
          <InstagramTab person={editedPerson} updateField={updateField} />
        )}
        {activeTab === 'osint' && (
          <OSINTTab person={editedPerson} addNotification={addNotification} />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-tech-bg/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="tech-card p-6 max-w-sm w-full">
            <div className="text-center mb-4">
              <div className="w-12 h-12 rounded bg-tech-danger/10 border border-tech-danger/30 flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-tech-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-mono font-semibold text-tech-text">CONFIRM DELETION</h3>
              <p className="text-tech-textMuted text-sm mt-2">
                Delete <span className="text-tech-text">{person.name}</span>? This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(false)}
                className="flex-1 tech-btn-secondary py-2"
              >
                CANCEL
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 py-2 px-4 bg-tech-danger text-white font-medium text-sm rounded hover:bg-tech-danger/90 transition"
              >
                DELETE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonDetails;
